import { Link } from 'react-router-dom'
import { Library, ClipboardList, BarChart3, ExternalLink, UserPlus, Wand2, ShieldCheck, ArrowRight, Sparkles } from 'lucide-react'
import { useAuthStore } from '@/stores/auth'
import { Card, CardContent } from '@/components/ui/card'
import { PageHeader } from '@/components/PageHeader'
import { Button } from '@/components/ui/button'
import { formatNivel } from '@/lib/roles'

/**
 * HomePage premium (F4) — saludo + hero CTA "Dar de alta cliente" + cuadrícula
 * de accesos rápidos. Sin jerga doctrinal. Sin emojis.
 */
export function HomePage() {
  const user = useAuthStore((s) => s.user)
  const contextOwners = useAuthStore((s) => s.contextOwners)
  const activeCoId = useAuthStore((s) => s.activeCoId)
  const activeCo = contextOwners.find((co) => co.id === activeCoId)
  const displayName = user?.nombre?.trim() || user?.email?.split('@')[0] || 'Arquitecto'

  return (
    <div className="space-y-8">
      <PageHeader
        title={`Hola, ${displayName}`}
        subtitle={
          activeCo
            ? `Operas como ${activeCo.nombre} · ${formatNivel(activeCo.nivel)}`
            : 'Sin organización activa'
        }
      />

      {/* Hero CTA — dar de alta un cliente */}
      <Card variant="elevated" className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-primary/5 to-transparent pointer-events-none" aria-hidden />
        <CardContent className="relative p-8 pt-8 flex flex-col sm:flex-row sm:items-center gap-6">
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-primary text-primary-foreground shadow-md">
            <UserPlus className="h-7 w-7" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-xl font-bold tracking-tight">Dar de alta un cliente nuevo</h2>
            <p className="text-sm text-muted-foreground mt-1 max-w-xl">
              Crea una app conector bajo tu organización: el contenedor (nombre, slug) y su
              interfaz UI/UX inicial. La app deriva su UI de los módulos contratados — se añaden
              después desde el marketplace.
            </p>
          </div>
          <Button asChild size="md" className="shrink-0">
            <Link to="/app/nuevo-cliente" className="gap-2">
              Comenzar
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
        </CardContent>
      </Card>

      {/* Accesos rápidos */}
      <section className="space-y-3">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground px-1">
          Accesos rápidos
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <QuickCard
            to="/app/generar-entidad"
            icon={Wand2}
            title="Crear módulo con IA"
            description="Describe en lenguaje natural y el asistente propone la estructura."
          />
          <QuickCard
            to="/app/catalogo"
            icon={Library}
            title="Catálogo de entidades"
            description="Explora las entidades disponibles en la tabla estándar y crea fichas."
          />
          <QuickCard
            to="/app/monitorizacion/fichas"
            icon={ClipboardList}
            title="Fichas (agregado)"
            description="Actividad agregada del organismo donde tienes lectura (solo lectura)."
          />
          <QuickCard
            to="/app/gobierno"
            icon={ShieldCheck}
            title="Gobierno y permisos"
            description="Organizaciones, personas, accesos, roles y contrataciones."
          />
        </div>
      </section>

      {/* Asistente + Monitorización */}
      <section className="grid sm:grid-cols-2 gap-4">
        <Card variant="interactive">
          <CardContent className="p-6 flex items-start gap-4">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <Sparkles className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <h3 className="text-base font-semibold">Asistente IA</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Pulsa <kbd className="px-1 py-0.5 rounded border border-border/60 bg-muted text-[10px] font-mono">Ctrl+J</kbd> o
                el botón "Asistente" arriba para abrir el chat. Lee documentación, responde dudas y
                ayuda a diseñar nuevos módulos.
              </p>
            </div>
          </CardContent>
        </Card>

        <a
          href="https://dashboard.aarpia-lite.es"
          target="_blank"
          rel="noopener noreferrer"
          className="block group"
        >
          <Card variant="interactive">
            <CardContent className="p-6 flex items-start gap-4">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <BarChart3 className="h-5 w-5" />
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="text-base font-semibold flex items-center gap-2">
                  Monitorización en vivo
                  <ExternalLink className="h-3.5 w-3.5 text-muted-foreground transition-colors group-hover:text-foreground" />
                </h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Paneles de Grafana con métricas en tiempo real del sistema: salud,
                  pipeline, catálogo, agentes IA y herramientas del asistente.
                </p>
              </div>
            </CardContent>
          </Card>
        </a>
      </section>
    </div>
  )
}

interface QuickCardProps {
  to: string
  icon: typeof Wand2
  title: string
  description: string
}

function QuickCard({ to, icon: Icon, title, description }: QuickCardProps) {
  return (
    <Link to={to} className="block group">
      <Card variant="interactive" className="h-full">
        <CardContent className="p-5">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary mb-3">
            <Icon className="h-5 w-5" />
          </div>
          <h3 className="text-sm font-semibold leading-tight">{title}</h3>
          <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">{description}</p>
        </CardContent>
      </Card>
    </Link>
  )
}
